import React, { useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import './StoreOwner.css';

const socket = io(config.SOCKET_URL, config.SOCKET_OPTIONS);

function StoreOwner() {
  const [formData, setFormData] = useState({ customerName: '', productName: '', amount: '' });
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ todayOrders: 0, todayPaid: 0, todayPending: 0, todayRevenue: 0 });
  const [filter, setFilter] = useState('ALL');
  const [timeFilter, setTimeFilter] = useState('TODAY');
  const [notifFilter, setNotifFilter] = useState('TODAY');
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const getApiUrl = (endpoint) => {
    const base = config.API_URL.replace(/\/+$/, '');
    return `${base}/api/${endpoint}`;
  };

  const isToday = (date) => {
    const d = new Date(date);
    const today = new Date();
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  };

  const fetchPaymentLinks = useCallback(async () => {
    try {
      const url = getApiUrl('orders');
      const response = await axios.get(url, { timeout: 15000, headers: { 'Accept': 'application/json' } });
      if (response.data.orders) {
        const orders = response.data.orders;
        setPaymentLinks(orders);
        setError(null);
        const todayOrders = orders.filter(o => isToday(o.createdAt || o.created_at));
        const todayPaid = todayOrders.filter(o => o.status === 'PAID');
        setStats({
          todayOrders: todayOrders.length,
          todayPaid: todayPaid.length,
          todayPending: todayOrders.filter(o => o.status === 'PENDING').length,
          todayRevenue: todayPaid.reduce((sum, o) => sum + Number(o.amount || 0), 0)
        });
      }
    } catch (err) {
      if (err.response) setError(`Server error: ${err.response.status}`);
      else if (err.request) setError('Cannot connect to server');
      else setError(err.message);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const url = getApiUrl('notifications');
      const response = await axios.get(url, { timeout: 10000 });
      if (response.data.notifications) setNotifications(response.data.notifications);
    } catch (err) {}
  }, []);

  const loadAllData = useCallback(async () => {
    setError(null);
    setDataLoaded(false);
    try { await Promise.all([fetchPaymentLinks(), fetchNotifications()]); } catch (err) {}
    setDataLoaded(true);
  }, [fetchPaymentLinks, fetchNotifications]);

  useEffect(() => {
    socket.on('connect', () => { setError(null); socket.emit('joinStoreOwner'); });
    socket.on('paymentSuccess', (data) => { toast.success(`💰 Payment of ₹${data.amount} received!`); loadAllData(); });
    socket.on('paymentLinkCreated', (data) => { toast.info(`🔗 Link created for ${data.customerName}`); loadAllData(); });
    loadAllData();
    return () => { socket.off('connect'); socket.off('paymentSuccess'); socket.off('paymentLinkCreated'); };
  }, [loadAllData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.productName || !formData.amount) { toast.error('Please fill all required fields'); return; }
    setLoading(true);
    try {
      const url = getApiUrl('create-payment-link');
      const response = await axios.post(url, {
        customerName: formData.customerName, productName: formData.productName, amount: parseFloat(formData.amount)
      }, { timeout: 15000, headers: { 'Content-Type': 'application/json' } });
      if (response.data.success) {
        toast.success('✅ Payment link created!');
        setFormData({ customerName: '', productName: '', amount: '' });
        if (response.data.paymentLink) { try { await navigator.clipboard.writeText(response.data.paymentLink); } catch (err) {} }
        loadAllData();
      } else { toast.error(response.data.message || 'Failed'); }
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to create link'); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };
  const copyToClipboard = async (link) => { try { await navigator.clipboard.writeText(link); toast.success('📋 Copied!'); } catch { toast.error('Failed to copy'); } };
  const shareWhatsApp = (link, name) => { window.open(`https://wa.me/?text=${encodeURIComponent(`Hi ${name}, your payment link: ${link}`)}`, '_blank'); };

  const filteredLinks = () => {
    let links = paymentLinks || [];
    if (timeFilter === 'TODAY') links = links.filter(l => isToday(l.createdAt || l.created_at));
    if (filter === 'ALL') return links;
    return links.filter(l => l.status === filter);
  };

  const filteredNotifications = () => {
    if (notifFilter === 'TODAY') return notifications.filter(n => isToday(n.created_at));
    return notifications;
  };

  const fmt = (amount) => '₹' + Number(amount || 0).toLocaleString('en-IN');
  const fmtDateTime = (d) => {
    if (!d) return 'N/A';
    try { return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return d; }
  };

  const statusStyle = (s) => {
    switch(s) {
      case 'PAID': return { bg: '#f0fdf4', c: '#166534', b: '#86efac', i: '✅', grad: 'linear-gradient(135deg, #f0fdf4, #dcfce7)' };
      case 'PENDING': return { bg: '#fffbeb', c: '#92400e', b: '#fcd34d', i: '⏳', grad: 'linear-gradient(135deg, #fffbeb, #fef3c7)' };
      case 'FAILED': return { bg: '#fef2f2', c: '#991b1b', b: '#fca5a5', i: '❌', grad: 'linear-gradient(135deg, #fef2f2, #fee2e2)' };
      default: return { bg: '#f9fafb', c: '#374151', b: '#d1d5db', i: '📋', grad: 'linear-gradient(135deg, #f9fafb, #f3f4f6)' };
    }
  };

  const c = (base, min, max) => `clamp(${min}, ${base}, ${max})`;

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: c('12px', '8px', '16px') }}>
        
        {/* Error */}
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '10px 14px', borderRadius: '10px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            <span style={{ color: '#991b1b', fontSize: c('0.8rem', '0.7rem', '0.85rem') }}>⚠️ {error}</span>
            <button onClick={loadAllData} style={{ padding: '5px 12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '0.75rem' }}>Retry</button>
          </div>
        )}

        {/* Header */}
        <div style={{ background: 'white', borderRadius: '14px', padding: c('16px', '12px', '20px'), marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <h1 style={{ fontSize: c('1.3rem', '1rem', '1.5rem'), fontWeight: 800, background: 'linear-gradient(135deg, #667eea, #764ba2)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            🏪 {config.APP_NAME}
          </h1>
          <p style={{ color: '#64748b', fontSize: c('0.8rem', '0.68rem', '0.88rem'), marginTop: '2px' }}>Create payment links & receive real-time notifications</p>
        </div>

        {/* Stats - Always 4 columns */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: c('6px', '3px', '10px'), marginBottom: '10px' }}>
          {[
            { icon: '📋', val: stats.todayOrders || 0, label: 'Orders', color: '#8b5cf6', bg: '#f5f3ff' },
            { icon: '✅', val: stats.todayPaid || 0, label: 'Paid', color: '#10b981', bg: '#ecfdf5' },
            { icon: '⏳', val: stats.todayPending || 0, label: 'Pending', color: '#f59e0b', bg: '#fffbeb' },
            { icon: '💰', val: fmt(stats.todayRevenue || 0), label: 'Revenue', color: '#3b82f6', bg: '#eff6ff' }
          ].map((s, i) => (
            <div key={i} style={{
              background: 'white', borderRadius: '10px', padding: c('10px', '6px', '14px'),
              display: 'flex', alignItems: 'center', gap: c('6px', '3px', '10px'),
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)', borderLeft: `3px solid ${s.color}`, minWidth: 0
            }}>
              <div style={{
                width: c('30px', '20px', '36px'), height: c('30px', '20px', '36px'),
                borderRadius: '6px', background: s.bg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: c('0.9rem', '0.65rem', '1.1rem'), flexShrink: 0
              }}>{s.icon}</div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <h3 style={{ fontSize: c('0.85rem', '0.62rem', '1rem'), fontWeight: 700, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.val}</h3>
                <p style={{ color: '#64748b', fontSize: c('0.62rem', '0.5rem', '0.68rem'), fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '10px' }}>
          
          {/* Create Form */}
          <div style={{ background: 'white', borderRadius: '14px', padding: c('16px', '12px', '20px'), boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <h2 style={{ fontSize: c('1rem', '0.85rem', '1.1rem'), fontWeight: 700, color: '#1e293b', marginBottom: '14px' }}>📝 Create Payment Link</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: c('0.75rem', '0.65rem', '0.8rem'), fontWeight: 600, color: '#475569', marginBottom: '3px' }}>Customer Name *</label>
                <input type="text" name="customerName" value={formData.customerName} onChange={handleChange} placeholder="Enter customer name" required
                  style={{ width: '100%', padding: c('9px', '7px', '11px'), border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: c('0.85rem', '0.75rem', '0.9rem'), fontFamily: "'Inter', sans-serif", background: '#f8fafc', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', fontSize: c('0.75rem', '0.65rem', '0.8rem'), fontWeight: 600, color: '#475569', marginBottom: '3px' }}>Product/Service *</label>
                <input type="text" name="productName" value={formData.productName} onChange={handleChange} placeholder="Enter product name" required
                  style={{ width: '100%', padding: c('9px', '7px', '11px'), border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: c('0.85rem', '0.75rem', '0.9rem'), fontFamily: "'Inter', sans-serif", background: '#f8fafc', outline: 'none' }} />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: c('0.75rem', '0.65rem', '0.8rem'), fontWeight: 600, color: '#475569', marginBottom: '3px' }}>Amount (₹) *</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontWeight: 700, color: '#667eea', fontSize: '0.9rem' }}>₹</span>
                  <input type="number" name="amount" value={formData.amount} onChange={handleChange} placeholder="0.00" required
                    style={{ width: '100%', padding: c('9px', '7px', '11px'), paddingLeft: '28px', border: '2px solid #e2e8f0', borderRadius: '8px', fontSize: c('0.9rem', '0.8rem', '1rem'), fontWeight: 600, fontFamily: "'Inter', sans-serif", background: '#f8fafc', outline: 'none' }} />
                </div>
              </div>
              <button type="submit" disabled={loading} style={{
                width: '100%', padding: c('10px', '8px', '12px'),
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #667eea, #764ba2)',
                color: 'white', border: 'none', borderRadius: '10px',
                fontSize: c('0.9rem', '0.78rem', '0.95rem'), fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}>{loading ? 'Creating...' : '🔗 Generate Payment Link'}</button>
            </form>
          </div>

          {/* Payment Links */}
          <div style={{ background: 'white', borderRadius: '14px', padding: c('16px', '12px', '20px'), boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '6px' }}>
              <h2 style={{ fontSize: c('1rem', '0.85rem', '1.1rem'), fontWeight: 700, color: '#1e293b' }}>📋 Links ({filteredLinks().length})</h2>
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '18px', padding: '2px' }}>
                  {['TODAY', 'ALL'].map(f => (
                    <button key={f} onClick={() => setTimeFilter(f)} style={{
                      padding: `${c('5px', '4px', '6px')} ${c('10px', '7px', '14px')}`,
                      border: 'none', borderRadius: '18px', cursor: 'pointer',
                      fontSize: c('0.7rem', '0.6rem', '0.75rem'), fontWeight: 600, whiteSpace: 'nowrap',
                      background: timeFilter === f ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent',
                      color: timeFilter === f ? 'white' : '#64748b'
                    }}>{f === 'TODAY' ? 'Today' : 'All'}</button>
                  ))}
                </div>
                <div style={{ position: 'relative' }}>
                  <select value={filter} onChange={(e) => setFilter(e.target.value)} style={{
                    padding: `${c('5px', '4px', '6px')} 24px ${c('5px', '4px', '6px')} 8px`,
                    border: '2px solid #e2e8f0', borderRadius: '18px',
                    fontSize: c('0.7rem', '0.6rem', '0.75rem'), fontWeight: 600, color: '#475569',
                    background: 'white', cursor: 'pointer', appearance: 'none', outline: 'none'
                  }}>
                    <option value="ALL">All Status</option>
                    <option value="PENDING">Pending</option>
                    <option value="PAID">Paid</option>
                    <option value="FAILED">Failed</option>
                  </select>
                  <span style={{ position: 'absolute', right: '7px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '0.5rem', color: '#94a3b8' }}>▼</span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', maxHeight: '400px', overflowY: 'auto', paddingRight: '3px' }}>
              {!dataLoaded ? (
                <div style={{ textAlign: 'center', padding: '30px' }}>
                  <div style={{ width: '26px', height: '26px', border: '3px solid #e2e8f0', borderTop: '3px solid #667eea', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }}></div>
                  <p style={{ marginTop: '10px', color: '#94a3b8', fontSize: '0.8rem' }}>Loading...</p>
                </div>
              ) : filteredLinks().length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '6px' }}>📭</div>
                  <p style={{ fontSize: '0.8rem' }}>No payment links found</p>
                </div>
              ) : (
                filteredLinks().map((link, idx) => {
                  const s = statusStyle(link.status);
                  return (
                    <div key={idx} style={{ background: s.grad, borderRadius: '10px', padding: '12px', borderLeft: `3px solid ${s.b}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ background: 'white', color: s.c, padding: '2px 7px', borderRadius: '12px', fontSize: '0.62rem', fontWeight: 700 }}>{s.i} {link.status}</span>
                        <span style={{ fontSize: c('1rem', '0.85rem', '1.1rem'), fontWeight: 700, color: '#1e293b' }}>{fmt(link.amount)}</span>
                      </div>
                      <div style={{ marginBottom: '4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                          <span style={{ color: '#64748b', fontSize: '0.65rem' }}>👤</span>
                          <span style={{ fontWeight: 500, color: '#334155', fontSize: c('0.75rem', '0.65rem', '0.8rem') }}>{link.customerName || link.customer_name}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                          <span style={{ color: '#64748b', fontSize: '0.65rem' }}>📦</span>
                          <span style={{ fontWeight: 500, color: '#334155', fontSize: c('0.75rem', '0.65rem', '0.8rem') }}>{link.productName || link.product_name}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                          <span style={{ color: '#64748b', fontSize: '0.65rem' }}>📅</span>
                          <span style={{ fontWeight: 500, color: '#64748b', fontSize: '0.65rem' }}>{fmtDateTime(link.createdAt || link.created_at)}</span>
                        </div>
                      </div>
                      {link.paymentLink && (
                        <>
                          <div style={{ background: 'white', padding: '5px 8px', borderRadius: '5px', marginBottom: '5px', border: '1px solid #e2e8f0' }}>
                            <code style={{ fontSize: '0.58rem', color: '#64748b', wordBreak: 'break-all' }}>{link.paymentLink}</code>
                          </div>
                          <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                            <button onClick={() => copyToClipboard(link.paymentLink)} style={{ flex: '1 1 50px', padding: '6px', border: 'none', borderRadius: '6px', background: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.68rem', color: '#475569' }}>📋</button>
                            <a href={link.paymentLink} target="_blank" rel="noopener noreferrer" style={{ flex: '1 1 50px', padding: '6px', border: 'none', borderRadius: '6px', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', textAlign: 'center', textDecoration: 'none', fontWeight: 600, fontSize: '0.68rem' }}>🔗</a>
                            <button onClick={() => shareWhatsApp(link.paymentLink, link.customerName || link.customer_name)} style={{ flex: '1 1 50px', padding: '6px', border: 'none', borderRadius: '6px', background: '#25D366', color: 'white', cursor: 'pointer', fontSize: '0.75rem' }}>💬</button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Notifications */}
        {notifications.length > 0 && (
          <div style={{ background: 'white', borderRadius: '14px', padding: c('16px', '12px', '20px'), boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginTop: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '6px' }}>
              <h2 style={{ fontSize: c('1rem', '0.85rem', '1.1rem'), fontWeight: 700, color: '#1e293b' }}>🔔 Notifications ({filteredNotifications().length})</h2>
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '18px', padding: '2px' }}>
                  {['TODAY', 'ALL'].map(f => (
                    <button key={f} onClick={() => setNotifFilter(f)} style={{
                      padding: `${c('4px', '3px', '5px')} ${c('9px', '6px', '12px')}`,
                      border: 'none', borderRadius: '18px', cursor: 'pointer',
                      fontSize: c('0.65rem', '0.55rem', '0.7rem'), fontWeight: 600, whiteSpace: 'nowrap',
                      background: notifFilter === f ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'transparent',
                      color: notifFilter === f ? 'white' : '#64748b'
                    }}>{f === 'TODAY' ? 'Today' : 'All'}</button>
                  ))}
                </div>
                <button onClick={fetchNotifications} style={{ padding: '4px 8px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem' }}>🔄</button>
              </div>
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredNotifications().slice(0, 15).map((n, i) => (
                <div key={i} style={{
                  display: 'flex', gap: '8px', padding: '8px 10px',
                  borderBottom: '1px solid #f1f5f9',
                  background: n.is_read ? 'transparent' : '#f8faff',
                  borderLeft: n.is_read ? 'none' : '3px solid #667eea'
                }}>
                  <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{n.type === 'PAYMENT_SUCCESS' ? '✅' : n.type === 'PAYMENT_FAILED' ? '❌' : '🔗'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: c('0.75rem', '0.65rem', '0.8rem'), color: '#1e293b', marginBottom: '2px' }}>{n.message}</p>
                    <span style={{ fontSize: '0.62rem', color: '#94a3b8' }}>🕐 {fmtDateTime(n.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        input:focus { border-color: #667eea !important; box-shadow: 0 0 0 3px rgba(102,126,234,0.1) !important; background: white !important; }
        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: repeat(4, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}

export default StoreOwner;